import Visibility from 'visibilityjs';
import PageLifecycle from 'page-lifecycle';
import { Action, action, Thunk, thunk, ThunkOn, thunkOn, State } from 'easy-peasy';
import { StoreState } from './store';
import { VHSService } from '../services/VHSService';
import { Browser } from '../utils/browser';
import { VHSEvent, VHSEventType, VHSEventDetails, SendVHSEventPayload, PlayerStatus, VideoType } from '../typings';

export interface VHSState {
  enabled: boolean;
  heartbeatInverval: number | null;
  heartbeatFrequency: number;
  heartbeatCount: number;
  playbackStarted: boolean;
  playbackBuffering: boolean;
  videoSwitching: boolean;
  setEnabled: Action<VHSState, boolean>;
  setHeartbeatInterval: Action<VHSState, number | null>;
  setHeartbeatCount: Action<VHSState, number>;
  setPlaybackStarted: Action<VHSState, boolean>;
  setPlaybackBuffering: Action<VHSState, boolean>;
  setVideoSwitching: Action<VHSState, boolean>;
  initialize: Thunk<VHSState, void, any, StoreState>;
  cleanup: Thunk<VHSState, void, any, StoreState>;
  reset: Action<VHSState>;
  startHeartbeat: Thunk<VHSState, void, any, StoreState>;
  stopHeartbeat: Thunk<VHSState, void, any, StoreState>;
  tickHeartbeat: Thunk<VHSState, void, any, StoreState>;
  handlePlayerPlaying: Thunk<VHSState, void, any, StoreState>;
  handlePlayerPaused: Thunk<VHSState, void, any, StoreState>;
  handlePlayerBuffering: Thunk<VHSState, void, any, StoreState>;
  handlePlayerEnded: Thunk<VHSState, void, any, StoreState>;
  handlePageStateChange: Thunk<VHSState, { newState: string; oldState: string }, any, StoreState>;
  sendVHSEvent: Thunk<VHSState, SendVHSEventPayload, any, StoreState, Promise<any>>;
  onPlayerStatusChanged: ThunkOn<VHSState, PlayerStatus, StoreState>;
  onPlayerFastForwardOrRewind: ThunkOn<VHSState, void, StoreState>;
  onResumeOverlayChanged: ThunkOn<VHSState, boolean, StoreState>;
  onUserInteraction: ThunkOn<VHSState, PlayerStatus, StoreState>;
  onVideoSwitched: ThunkOn<VHSState, boolean, StoreState>;
  onPlayerDestroy: ThunkOn<VHSState, boolean, StoreState>;
}

const initialState: State<VHSState> = {
  /** Indicates if VHS functionality should be performed. */
  enabled: false,

  /** Stores a setInterval ID used for heartbeat event. */
  heartbeatInverval: null,

  /** Indicates the frecuency for sending the heartbeat event. */
  heartbeatFrequency: 30,

  /** Accumulates the viewing time passed until heartbeatFrequency is reached. */
  heartbeatCount: 0,

  /**
   * Indicates if the current video has started playing.
   * This state resets if the video data is changed.
   **/
  playbackStarted: false,

  /**
   * Indicates if the video is buffering.
   * Helps to determine which VHS event to send after video plays again.
   **/
  playbackBuffering: false,

  /**
   * Indicates if the video is switched.
   * Helps to determine if the user changed between workout and modifier videos.
   **/
  videoSwitching: false,
};

export const vhsState: VHSState = {
  /*================
   * State      
   ================*/
  ...initialState,

  /*================
   * Actions      
   ================*/

  initialize: thunk((actions, _, { getStoreState }) => {
    const storeState = getStoreState();

    if (!storeState.config.userGuid) return;

    PageLifecycle.addEventListener('statechange', actions.handlePageStateChange);

    actions.startHeartbeat();
    actions.setEnabled(true);
  }),

  cleanup: thunk((actions, _, { getState, getStoreState }) => {
    const storeState = getStoreState();

    if (!shouldSkipVHS(storeState) && [PlayerStatus.Playing, PlayerStatus.Paused].includes(storeState.player.status)) {
      actions.sendVHSEvent({
        eventType: VHSEventType.stop,
        eventDetails: VHSEventDetails.media_end,
      });
    }

    PageLifecycle.removeEventListener('statechange', actions.handlePageStateChange);

    actions.stopHeartbeat();
    actions.setEnabled(false);
  }),

  reset: action(state => {
    Object.assign(state, { ...initialState });
  }),

  setEnabled: action((state, enabled) => {
    state.enabled = enabled;
  }),

  setHeartbeatInterval: action((state, heartbeatInverval) => {
    state.heartbeatInverval = heartbeatInverval;
  }),

  setHeartbeatCount: action((state, heartbeatCount) => {
    state.heartbeatCount = heartbeatCount;
  }),

  setPlaybackStarted: action((state, playbackStarted) => {
    state.playbackStarted = playbackStarted;
  }),

  setPlaybackBuffering: action((state, playbackBuffering) => {
    state.playbackBuffering = playbackBuffering;
  }),

  setVideoSwitching: action((state, videoSwitched) => {
    state.videoSwitching = videoSwitched;
  }),

  startHeartbeat: thunk(async actions => {
    actions.stopHeartbeat();
    const intervalId = setInterval(actions.tickHeartbeat, 1000);
    actions.setHeartbeatInterval(Number(intervalId));
  }),

  stopHeartbeat: thunk(async (actions, _, { getState }) => {
    const state = getState();
    clearInterval(state.heartbeatInverval || 0);
    actions.setHeartbeatInterval(null);
  }),

  /**
   * Increments the viewing time and
   * sends the heartbeat VHS event.
   **/
  tickHeartbeat: thunk(async (actions, _, { getState, getStoreState }) => {
    const state = getState();
    const storeState = getStoreState();

    if (shouldSkipVHS(storeState)) return;

    if (storeState.player.status !== PlayerStatus.Playing) return;

    if (state.heartbeatCount + 1 === state.heartbeatFrequency) {
      actions.setHeartbeatCount(0);
      await actions.sendVHSEvent({
        eventType: VHSEventType.heartbeat,
        eventDetails: VHSEventDetails.system_generated,
      });
    } else {
      actions.setHeartbeatCount(state.heartbeatCount + 1);
    }
  }),

  handlePlayerPlaying: thunk(async (actions, _, { getState, getStoreState }) => {
    const state = getState();
    const storeState = getStoreState();
    const { videoItem, resumeOverlayVisible } = storeState.player;

    // Skip VHS Events when the Resume Overlay screen is visible.
    if (resumeOverlayVisible) return;

    // Restart the interval every time the player resumes playback
    actions.startHeartbeat();

    if (!state.playbackBuffering || state.videoSwitching) {
      if (state.videoSwitching && videoItem?.type === VideoType.MODIFIER) {
        actions.sendVHSEvent({
          eventType: VHSEventType.play,
          eventDetails: VHSEventDetails.modifier_video,
        });
      } else if (state.videoSwitching && videoItem?.type === VideoType.WORKOUT) {
        actions.sendVHSEvent({
          eventType: VHSEventType.play,
          eventDetails: VHSEventDetails.source_video,
        });
      }

      if (!state.playbackStarted) {
        actions.sendVHSEvent({
          eventType: VHSEventType.start,
          eventDetails: VHSEventDetails.system_generated,
        });
      }
    }

    actions.setPlaybackStarted(true);
    actions.setPlaybackBuffering(false);
    actions.setVideoSwitching(false);
  }),

  handlePlayerPaused: thunk(async actions => {
    actions.setHeartbeatCount(0);
  }),

  handlePlayerBuffering: thunk(async (actions, _, { getState }) => {
    const state = getState();

    // Track buffering after the playback starts.
    if (state.playbackStarted) {
      actions.setPlaybackBuffering(true);
    }
  }),

  handlePlayerEnded: thunk(async actions => {
    actions.sendVHSEvent({
      eventType: VHSEventType.stop,
      eventDetails: VHSEventDetails.media_end,
    });

    // Reset for next video playback
    actions.setPlaybackStarted(false);
    actions.setPlaybackBuffering(false);
    actions.setHeartbeatCount(0);
  }),

  handlePageStateChange: thunk((actions, event) => {
    if (event.newState === 'terminated') {
      actions.handlePlayerEnded();
    }
  }),

  /**
   * Reusable action to send all VHS Events.
   * Receives the specific VHSEventType and VHSEventDetails
   * and grabs common event parameters from the storeState.
   */
  sendVHSEvent: thunk(async (_, { eventType, eventDetails }, { getStoreState }) => {
    const storeState = getStoreState();

    if (!storeState.config.userGuid) return;

    function getSelectedSubtitle() {
      return storeState.subtitles.activeSubtitle?.label || undefined;
    }

    function getDuration() {
      let duration = storeState.player.playbackDurationTime;
      return Math.floor(duration);
    }

    function getTimecode() {
      let timecode = storeState.player.playbackCurrentTime;

      // If the video is switching to modifier, use that as the timecode
      if (!timecode && storeState.player.playbackSwitchTime) {
        timecode = storeState.player.playbackSwitchTime;
      }

      return Math.round(timecode);
    }

    /**
     * Returns the current video GUID.
     * If the current video is a modifier, returns
     * the main video GUID.
     */
    function getVideoGuid() {
      if (storeState.player.videoItem?.type === VideoType.MODIFIER) {
        return String(storeState.videos.workoutVideo?.guid);
      } else {
        return String(storeState.player.videoItem?.guid);
      }
    }

    const event: VHSEvent = {
      eventType,
      eventDetails,
      profileID: String(storeState.config.userGuid),
      videoGuid: getVideoGuid(),
      duration: getDuration(),
      timecode: getTimecode(),
      audioTrack: String(storeState.player.audioTrack?.label || 'default'),
      subtitlesOn: getSelectedSubtitle() ? true : false,
      subtitleTrack: getSelectedSubtitle(),
      platform: 'web',
      platformDetails: `${Browser.info.name} - ${Browser.os.name}`,
      heartbeatInterval: storeState.vhs.heartbeatFrequency,
      timestamp: new Date().toISOString(),
      playerVisible: !Visibility.hidden(),
      entityId: storeState.config.entityId || undefined,
      recommendationId: storeState.config.recommendationId || undefined,
      ...storeState.config.extraHeartbeatParams,
    };

    return await VHSService.sendEvent(storeState.config, event);
  }),

  /*================
   * Listeners      
   ================*/

  onPlayerStatusChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setStatus];
    },
    function handler(actions, event, { getStoreState }) {
      const storeState = getStoreState();

      if (shouldSkipVHS(storeState)) return;

      const playerStatus = event.payload;

      switch (playerStatus) {
        case PlayerStatus.Paused:
          return actions.handlePlayerPaused();
        case PlayerStatus.Playing:
          return actions.handlePlayerPlaying();
        case PlayerStatus.Buffering:
          return actions.handlePlayerBuffering();
        case PlayerStatus.Ended:
          return actions.handlePlayerEnded();
      }
    }
  ),

  onPlayerFastForwardOrRewind: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.fastForward, storeActions.player.rewind];
    },
    function handler(actions, _, { getStoreState }) {
      const storeState = getStoreState();

      if (shouldSkipVHS(storeState)) return;

      switch (storeState.player.status) {
        case PlayerStatus.Playing:
          actions.sendVHSEvent({
            eventType: VHSEventType.play,
            eventDetails: VHSEventDetails.user_generated,
          });
          break;
        case PlayerStatus.Paused:
          actions.sendVHSEvent({
            eventType: VHSEventType.pause,
            eventDetails: VHSEventDetails.user_generated,
          });
          break;
      }
    }
  ),

  onUserInteraction: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.interactions.play, storeActions.interactions.pause];
    },
    function handler(actions, event, { getStoreActions }) {
      const storeActions = getStoreActions();

      switch (event.type) {
        case storeActions.interactions.play.type:
          actions.sendVHSEvent({
            eventType: VHSEventType.play,
            eventDetails: VHSEventDetails.user_generated,
          });
          break;
        case storeActions.interactions.pause.type:
          actions.sendVHSEvent({
            eventType: VHSEventType.pause,
            eventDetails: VHSEventDetails.user_generated,
          });
          break;
      }
    }
  ),

  onResumeOverlayChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setResumeOverlayVisible];
    },
    function handler(actions, data, { getStoreState }) {
      const storeState = getStoreState();

      if (shouldSkipVHS(storeState)) return;

      const resumeVisible = data.payload;

      if (resumeVisible) {
        actions.sendVHSEvent({
          eventType: VHSEventType.pause,
          eventDetails: VHSEventDetails.resume_show,
        });
      } else {
        actions.sendVHSEvent({
          eventType: VHSEventType.play,
          eventDetails: VHSEventDetails.resume_hide,
        });
      }
    }
  ),

  onVideoSwitched: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.videos.switchVideo];
    },
    function handler(actions, _, { getStoreState }) {
      const storeState = getStoreState();

      if (shouldSkipVHS(storeState)) return;

      actions.setVideoSwitching(true);
    }
  ),

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy.startType];
    },
    function handler(actions) {
      actions.cleanup();
      actions.reset();
    }
  ),
};

/**
 * This helper determines if the VHS functionality should be skipped
 * even if the VHS feature is generally enabled.
 */
function shouldSkipVHS(storeState: State<StoreState>) {
  let shouldSkipVHS = false;

  // Skip VHS if the feature is not enabled.
  if (!storeState.vhs.enabled) {
    shouldSkipVHS = true;
  }

  // Skip VHS if casting.
  if (storeState.chromecast.castSession) {
    shouldSkipVHS = true;
  }

  return shouldSkipVHS;
}
